/**
 * ReferralTransactions
 *
 * Modal that fetches and displays the referee and referrer transactions
 * for a given referral, allowing admins to verify eligibility.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS } from './constants';
import { colors, modalStyles, commonStyles } from '../AgenticPaymentTransactionHistory/styles';
import { formatReferralDate } from './utils';

// ── Helpers ───────────────────────────────────────────────────────────────────

const thStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  color: '#fff',
  fontWeight: 600,
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)',
  borderBottom: '2px solid #e58734',
};

const tdStyle = {
  padding: '10px 14px',
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle',
  fontSize: '13px',
  color: colors.gray700,
};

const TYPE_COLORS = {
  deposit:  { background: '#d4edda', color: '#155724' },
  transfer: { background: '#cce5ff', color: '#004085' },
  swap:     { background: '#fff3cd', color: '#856404' },
  trade:    { background: '#e2d9f3', color: '#4a235a' },
};

const StatusBadge = ({ status }) => {
  const ok = status === 'completed';
  return (
    <span style={{
      padding: '3px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 600,
      background: ok ? '#d4edda' : '#f8d7da',
      color: ok ? '#155724' : '#721c24',
    }}>
      {status}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const style = TYPE_COLORS[type] || { background: colors.gray200, color: colors.gray700 };
  return (
    <span style={{ ...style, padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>
      {type}
    </span>
  );
};

// Render the key fields for each transaction type
const TxDetails = ({ tx }) => {
  const rows = [];

  if (tx.amount != null) {
    const label = tx.type === 'swap' ? 'From Amount' : 'Amount';
    const val   = tx.currency ? `${tx.amount} ${tx.currency}` : String(tx.amount);
    rows.push([label, val]);
  }
  if (tx.toAmount != null)    rows.push(['To Amount', `${tx.toAmount}${tx.toTokenId ? ` (${tx.toTokenId})` : ''}`]);
  if (tx.fromTokenId)         rows.push(['From Token', tx.fromTokenId]);
  if (tx.toTokenId && !tx.toAmount) rows.push(['To Token', tx.toTokenId]);
  if (tx.tokenId)             rows.push(['Token', tx.tokenId]);
  if (tx.exchangeRate != null) rows.push(['Exchange Rate', tx.exchangeRate.toLocaleString()]);
  if (tx.tradeType)           rows.push(['Trade Type', tx.tradeType]);
  if (tx.tradeState)          rows.push(['Trade State', tx.tradeState]);
  if (tx.senderWalletAddress) rows.push(['Sender', tx.senderWalletAddress]);
  if (tx.recipientWalletAddress) rows.push(['Recipient', tx.recipientWalletAddress]);
  if (tx.memo)                rows.push(['Memo', tx.memo]);
  if (tx.transactionHash)     rows.push(['TX Hash', tx.transactionHash]);

  return (
    <div style={{ fontSize: '12px', color: colors.gray600 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
          <span style={{ fontWeight: 600, minWidth: '90px', color: colors.primary }}>{k}:</span>
          <code style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{v}</code>
        </div>
      ))}
    </div>
  );
};

// ── Party Section ─────────────────────────────────────────────────────────────

// API now returns flat arrays: referee_transactions / referrer_transactions
const PartySection = ({ transactions, label }) => {
  const txList = transactions || [];

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        background: 'linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)',
        borderRadius: '4px 4px 0 0',
        borderBottom: '2px solid #e58734',
      }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
          {txList.length} transaction{txList.length !== 1 ? 's' : ''}
        </span>
      </div>

      {txList.length > 0 ? (
        <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Type', 'Status', 'Date', 'Details'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txList.map((tx, idx) => (
                <tr key={tx.id || idx} style={{ background: idx % 2 === 0 ? colors.white : colors.bgLight }}>
                  <td style={tdStyle}><TypeBadge type={tx.type} /></td>
                  <td style={tdStyle}><StatusBadge status={tx.status} /></td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatReferralDate(tx.createdAt)}</td>
                  <td style={tdStyle}><TxDetails tx={tx} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: '20px', textAlign: 'center', color: colors.gray500, fontSize: '13px', border: `1px solid ${colors.border}`, borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
          No transactions found.
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const ReferralTransactions = ({ referralId, referralExtId, onClose }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getRequest(API_ENDPOINTS.TRANSACTIONS(referralId));
      if (!result.success) throw new Error(result.error || 'Failed to load transactions');
      setData(result.data);
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [referralId]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div
        style={{ ...modalStyles.content, maxWidth: '860px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={modalStyles.header}>
          <div>
            <h3 style={modalStyles.headerTitle}>Transactions</h3>
            {referralExtId && (
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>
                Referral: {referralExtId}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} style={modalStyles.closeButton}>
            ✕ Close
          </button>
        </div>

        {/* Body */}
        <div style={modalStyles.body}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ width: '32px', height: '32px', border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <p style={{ color: colors.gray600, fontSize: '14px', margin: 0 }}>Loading transactions...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '20px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{error}</span>
              <button type="button" onClick={fetchTransactions} style={{ padding: '6px 14px', background: '#721c24', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <PartySection transactions={data?.referee_transactions}  label="Referee Transactions" />
              <PartySection transactions={data?.referrer_transactions} label="Referrer Transactions" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralTransactions;
